import {
  createContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';

import { Notification } from '@/components';
import { NotificationProps } from '@/components/notifications/Notification';

export interface NotificationContextProps {
  showNotification: (
    message: string,
    severity: NotificationProps['severity'],
    duration?: number
  ) => void;
}

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [queue, setQueue] = useState<
    Omit<NotificationProps, 'open' | 'onClose'>[]
  >([]);
  const [currentNotification, setCurrentNotification] = useState<Omit<
    NotificationProps,
    'open' | 'onClose'
  > | null>(null);
  const [open, setOpen] = useState(false);

  const showNotification = useCallback(
    (
      message: string,
      severity: NotificationProps['severity'],
      duration = 6500
    ) => {
      setQueue((prev) => [...prev, { message, severity, duration }]);
    },
    []
  );

  const processQueue = useCallback(() => {
    if (queue.length > 0) {
      const nextNotification = queue[0];
      setCurrentNotification(nextNotification);
      setQueue((prev) => prev.slice(1));
      setOpen(true);
    }
  }, [queue]);

  const handleClose = useCallback(
    (_: React.SyntheticEvent, reason?: string) => {
      if (reason === 'clickaway') {
        // Evitar cierre al hacer click fuera
        return;
      }
      setOpen(false);
      setTimeout(() => {
        setCurrentNotification(null);
      }, 250); // Retraso para animación de cierre
    },
    [processQueue]
  );

  useEffect(() => {
    if (!currentNotification && queue.length > 0) {
      processQueue();
    }
  }, [open, currentNotification, queue, processQueue]);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      {currentNotification && (
        <Notification
          open={open}
          message={currentNotification.message}
          severity={currentNotification.severity}
          duration={currentNotification.duration}
          // @ts-ignore
          onClose={handleClose}
        />
      )}
    </NotificationContext.Provider>
  );
};

export const NotificationContext = createContext<
  NotificationContextProps | undefined
>(undefined);
